/**
 * Password validation regex requiring minimum 12 characters with:
 * - At least one uppercase letter (A-Z)
 * - At least one lowercase letter (a-z)
 * - At least one digit (0-9)
 * - At least one special character (#?!@$%^&*-)
 */
export const PASSWORD_REGEX_12 =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{12,}$/;

/**
 * Password validation regex requiring minimum 8 characters with:
 * - At least one uppercase letter (A-Z)
 * - At least one lowercase letter (a-z)
 * - At least one digit (0-9)
 * - At least one special character (#?!@$%^&*-)
 */
export const PASSWORD_REGEX_8 =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

/**
 * Matches any uppercase letter (A-Z)
 */
export const UPPERCASE_REGEX = /[A-Z]/;

/**
 * Matches common special characters: !@#$%^&*()
 */
export const SPECIAL_CHARACTERS_REGEX = /[!@#$%^&*()]/;

/**
 * Matches any numeric digit (0-9)
 */
export const NUMERIC_REGEX = /[0-9]/;

/**
 * Validates minimum length of 8 characters
 */
export const MIN_LENGTH_REGEX = /^.{8,}$/;

/**
 * cPanel email password validation requiring minimum 12 characters with:
 * - At least one uppercase letter (A-Z)
 * - At least one lowercase letter (a-z)
 * - At least one digit (0-9)
 * - At least one special character (#?!@$%^&*-)
 * - Only printable ASCII characters (prevents UTF-8/Unicode)
 */
export const CPANEL_EMAIL_PASSWORD_REGEX =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[#?!@$%^&*-])[\x20-\x7E]{12,}$/;

/**
 * SSH username validation: lowercase letters, digits, and special chars (_.@,-)
 */
export const SSH_USER_NAME_REGEX = /^[a-z0-9_.@,-]+$/;

/**
 * Basic username validation: alphanumeric characters only
 */
export const USER_NAME_REGEX = /^[a-zA-Z0-9]+$/;

/**
 * Username with underscores: alphanumeric segments separated by underscores
 */
export const USER_NAME_WITH_UNDERSCORES_REGEX =
  /^[a-zA-Z0-9]+(?:_[a-zA-Z0-9]+)*$/;

/**
 * UNIX username validation: starts with letter/underscore, allows dots and hyphens
 */
export const UNIX_USERNAME_REGEX = /^[a-z_][a-z0-9_.-]+$/;

/**
 * Username with optional dot: alphanumeric with underscores, optional single dot
 */
export const USERNAME_WITH_OPTIONAL_DOT_REGEX =
  /^[a-zA-Z0-9_]+([.]?[a-zA-Z0-9_]+)?$/;

/**
 * SSH public key validation: matches the base64 portion starting with "AAAA"
 */
export const SSH_PUBLIC_KEY_REGEX = new RegExp('AAAA[A-Za-z0-9/+]+=*');

/**
 * Detects consecutive characters in passwords (both ascending and descending):
 * - Numeric sequences: 01, 12, 23... or 98, 87, 76...
 * - Alphabetic sequences: ab, bc, cd... or zy, yx, xw...
 */
export const CONSECUTIVE_CHARACTERS_REGEX =
  /01|12|23|34|45|56|67|78|89|98|87|76|65|54|43|32|21|ab|bc|cd|de|ef|fg|gh|hi|ij|jk|kl|lm|mn|no|op|pq|qr|rs|st|tu|uv|vw|wx|xy|yz|zy|yx|xw|wv|vu|ut|ts|sr|rq|qp|po|on|nm|ml|lk|kj|ji|ih|hg|gf|fe|ed|dc|cb|ba/i;

/**
 * Detects common adjacent keyboard key patterns (QWERTY layout):
 * - Top row: qwe, wer, ert, rty, tyu, yui, uio, iop
 * - Middle row: asd, sdf, dfg, fgh, ghj, hjk, jkl
 * - Bottom row: zxc, xcv, cvb, vbn, bnm
 */
export const ADJACENT_KEYBOARD_KEYS_REGEX =
  /qwe|wer|ert|rty|tyu|yui|uio|iop|asd|sdf|dfg|fgh|ghj|hjk|jkl|zxc|xcv|cvb|vbn|bnm/i;

/**
 * Detects identical consecutive characters (e.g., "aa", "11", "@@")
 */
export const IDENTICAL_CHARACTERS_REGEX = /(.)\1/i;

/**
 * Basic URL validation without protocol:
 * - Domain with subdomains
 * - Optional port number
 * - Optional path
 */
export const URL_REGEX =
  /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}(:\d+)?(\/[^\s]*)?$/;

/**
 * Strict IPv4 address validation:
 * - Each octet must be 0-255
 * - Validates proper dotted decimal notation
 * - Rejects leading zeros (e.g., 192.168.001.1)
 */
export const IP_REGEX =
  /^(25[0-5]|2[0-4]\d|1?\d\d?)\.(25[0-5]|2[0-4]\d|1?\d\d?)\.(25[0-5]|2[0-4]\d|1?\d\d?)\.(25[0-5]|2[0-4]\d|1?\d\d?)$/;

/**
 * Comprehensive IPv6 address validation supporting:
 * - Full notation (8 groups of 4 hex digits)
 * - Compressed notation with ::
 * - Mixed IPv4-mapped IPv6 addresses
 * - Link-local addresses with zone ID
 * - Various shorthand formats
 */
export const IPV6_REGEX =
  /^(([0-9a-fA-F]{1,4}:){7}([0-9a-fA-F]{1,4})|(([0-9a-fA-F]{1,4}:){1,7}:)|(([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4})|(([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2})|(([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3})|(([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4})|(([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5})|([0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6}))|(:((:[0-9a-fA-F]{1,4}){1,7}|:))|(fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,})|(::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3,3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3,3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))$/;

/**
 * Mail server hostname validation:
 * - Must start with alphanumeric character
 * - Allows dots and hyphens in hostname
 * - Must end with valid TLD (2+ characters)
 */
export const MAIL_SERVER_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9.-]*\.)+[a-zA-Z]{2,}$/;

/**
 * Alias validation for various services:
 * - Alphanumeric characters
 * - Dots, hyphens, and underscores allowed
 */
export const ALIAS_REGEX = /^[a-zA-Z0-9.\-_]+$/;

/**
 * Service name validation for DNS SRV records:
 * - Must start with underscore (_)
 * - Followed by alphabetical characters only
 */
export const SERVICE_REGEX = /^_[A-Za-z]+$/;

/**
 * Fully qualified domain name validation:
 * - Domain with subdomains (no protocol)
 * - Must end with valid TLD (2+ characters)
 * - Example: example.domain.com
 */
export const TARGET_EXP_REGEX = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/;

/**
 * Simplified IPv4 validation (less strict than IP_REGEX):
 * - Basic format validation for four octets
 * - Allows leading zeros
 * - Quick validation for less critical use cases
 */
export const SIMPLE_IPV4_REGEX =
  /^(?:(?:^|\.)(?:2(?:5[0-5]|[0-4]\d)|1?\d?\d)){4}$/;

/**
 * Simplified IPv6 validation (less strict than IPV6_REGEX):
 * - Basic hexadecimal and colon pattern matching
 * - More permissive than full IPv6 specification
 * - Suitable for initial input validation
 */
export const SIMPLE_IPV6_REGEX = /^([0-9a-f]|:){1,4}(:([0-9a-f]{0,4})*){1,7}$/i;

/**
 * Keyboard input validation for numeric fields:
 * - Allows digits (0-9)
 * - Allows navigation keys: Backspace, Tab, Delete
 * - Allows arrow keys: Left, Right, Up, Down
 */
export const NUMERIC_KEYBOARD_INPUT_REGEX = new RegExp(
  /(^\d*$)|(Backspace|Tab|Delete|ArrowLeft|ArrowRight|ArrowUp|ArrowDown)/,
);

/**
 * Domain validation patterns for different use cases
 */
export const DOMAIN_PATTERNS = {
  /**
   * Strict domain validation:
   * - Supports internationalized domains (xn-- prefix)
   * - Validates label length limits (63 chars)
   * - Ensures proper domain structure
   * - Case-insensitive (DNS hostnames are not case-sensitive)
   */
  DOMAIN:
    /^((?!-))(xn--)?[a-z0-9][a-z0-9-_]{0,61}[a-z0-9]{0,1}\.(xn--)?([a-z0-9-]{1,61}|[a-z0-9-]{1,30}\.[a-z]{2,})$/i,

  /**
   * Domain with optional protocol and www:
   * - Accepts http/https protocols
   * - Optional www subdomain
   * - Allows various special characters in path
   */
  DOMAIN_WITH_SCHEMA:
    /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/,

  /**
   * Full URL validation with path:
   * - Optional protocol and www
   * - Proper domain structure
   * - Optional port number
   * - Optional path with safe characters
   */
  DOMAIN_PATH:
    /^(?:https?:\/\/)?(?:www\.)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?::\d+)?(?:\/[\w~\-.]*)*$/,

  /**
   * Detects Unicode characters outside basic Latin range:
   * - Matches any character outside U+0020 to U+00FF
   * - Used to identify non-ASCII domain characters
   */
  UNICODE: /[^\u0020-\u00ff]/,
};

/**
 * Matches files with .pub extension (SSH public key files)
 */
export const PUB_EXTENSION_REGEX = /\.pub$/;

/**
 * Site path validation:
 * - Digits (0-9), letters (A-Z, a-z)
 * - Special characters: . - _ ~ /
 * - Used for validating URL paths and site directories
 */
export const SITE_PATH_REGEX = /^([-a-zA-Z0-9/._~]*)$/gm;
